/**
 * A queue structure that helps run asynchronous luminary tasks which draw or stop drawing to the canvas.
 *
 * NOTE: This library exposes ONE queue that is meant to run luminary tasks all using the SAME Luminary instance.
 * If the entry point uses more than one luminary instance, then this queue should only enqueue tasks belonging to
 * ONE Luminary instance.
 *
 * This queue ensures that these tasks are run sequentially in the order they are enqueued, and that
 * a task of the same type is never enqueued one after another.
 */

type QueueTask = {
    callback: (...args: unknown[]) => Promise<void>;
    args: unknown[];
    taskName: TASK_NAMES;
};

let queue: QueueTask[] = [];
let isProcessingQueue = false;

const DEBUG = false;

export enum TASK_NAMES {
    START_PLAYBACK = 'startPlayback',
    STOP_PLAYBACK = 'stopPlayback',
    START_SCENE_PLAYBACK = 'startScenePlayback',
    START_TEXT_EDITING = 'startTextEditing',
    STOP_TEXT_EDITING = 'stopTextEditing',
    LOAD_VIDEO_PREVIEW = 'loadVideoPreview',
    VIDEO_PREVIEW_SEEK_TO_START = 'videoPreviewSeekToStart',
    UPDATE_PREVIEW_AT_TIME = 'updatePreviewAtTime',
}

/**
 * Stores a task to be run later. Calling this function will not immediately run the task.
 * The task will not be enqueued if the last enqueued task shares the same task name.
 *
 * @param opts.callback the async task to be queued and run
 * @param [opts.args] any arguments to be suppled to the callback
 * @param opts.taskName one of the enums defined in TASK_NAMES
 * @param [opts.runQueue] whether or not to immediately process the tasks in the queue
 */
export function enqueueTask({
    callback,
    args = [],
    taskName,
    runQueue = true,
}: {
    callback: (...args: any[]) => Promise<void>;
    args?: unknown[];
    taskName: TASK_NAMES;
    runQueue?: boolean;
}) {
    const tailTask = getQueueTail();
    if (tailTask?.taskName === taskName && taskName === TASK_NAMES.START_TEXT_EDITING) {
        return;
    }
    queue.push({ callback, args, taskName });
    if (runQueue) {
        processQueue();
    }
}

/**
 * Runs all tasks that are stored in the queue. In the event of an unrecoverable error,
 * the entire queue is cleared and the error is thrown.
 */
export async function processQueue() {
    if (isProcessingQueue) {
        if (DEBUG) {
            // eslint-disable-next-line no-console
            console.warn(
                'The queue is currently being processed:',
                JSON.stringify(getQueueTaskNames()),
            );
        }
        return;
    }
    try {
        isProcessingQueue = true;
        while (queue.length) {
            const task = queue[0];
            await task.callback(...task.args);
            queue.shift();
        }
        isProcessingQueue = false;
    } catch (e) {
        const forceClear = true;
        clearQueue(forceClear);
        throw e;
    }
}

/**
 * Waits for the queue to finish processing.
 * @returns A promise that resolves when the queue is empty and not processing.
 */
export async function waitForQueueToFinish(): Promise<void> {
    if (!isProcessingQueue && queue.length === 0) {
        return;
    }

    return new Promise<void>((resolve) => {
        const checkQueue = () => {
            if (!isProcessingQueue && queue.length === 0) {
                resolve();
            } else {
                setTimeout(checkQueue, 50);
            }
        };
        checkQueue();
    });
}

export function clearQueue(forceClear?: boolean) {
    if (isProcessingQueue && !forceClear) {
        // eslint-disable-next-line no-console
        console.warn('The queue is still processing and cannot be cleared.');
        return;
    }
    queue = [];
    isProcessingQueue = false;
}

function getQueueTail() {
    if (!queue.length) {
        return null;
    }
    return queue.at(-1);
}

function getQueueTaskNames() {
    return queue.map((task) => task.taskName);
}

export function getLuminaryTaskQueue() {
    return queue;
}
